import { useMemo, useState } from 'react'
import { BalanceAfter } from '../components/pos/BalanceAfter'
import { Skeleton } from '../components/Skeleton'
import { useAuth } from '../context/AuthContext'
import { useCari } from '../context/CariContext'
import { addEntries, deleteDebtor, saveDebtor, type NewEntry } from '../services/cari'
import { amountInput, applyDiscount, formatTRY, liraToKurus } from '../utils/money'
import { InfoIcon, PlusIcon, SearchIcon, TrashIcon } from '../components/icons'
import { OrderInfoDialog } from '../components/pos/OrderInfoDialog'
import { Spinner } from '../components/Spinner'
import type { DebtorBalance } from '../types'
import './CariPage.css'

const fold = (s: string) => s.toLocaleLowerCase('tr-TR')

/** "12,5" → 12.5; 0'dan büyük ve en çok 100 değilse null. */
const parsePct = (text: string): number | null => {
  const v = Number(text.replace(',', '.'))
  return text.trim() && Number.isFinite(v) && v > 0 && v <= 100 ? Math.round(v * 100) / 100 : null
}
const pctText = (p: number) => `%${String(p).replace('.', ',')}`

const asDateTime = (ms: number) =>
  new Date(ms).toLocaleString('tr-TR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * Accounts and what is owed on them.
 *
 * Two columns: everyone on the left, one person's history on the right. The
 * history is the point, a balance on its own invites an argument, and the
 * only answer to "I already paid that" is the list of movements with dates
 * against them.
 */
export function CariPage() {
  const { tenantId, admin, canSettle, isOwner, discounts } = useAuth()
  const { debtors, entriesFor, totalOwedKurus, loading } = useCari()

  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newNote, setNewNote] = useState('')

  /** The movement being added, when one is. */
  const [entryKind, setEntryKind] = useState<'debt' | 'payment' | 'restructure' | null>(null)
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  /**
   * "Borcu yapılandır"da uygulanan oran (kullanıcı, 11 Eki 2026): kasadaki
   * hazır oranlardan biri ya da elle yazılan. null = seçilmedi.
   */
  const [discountPct, setDiscountPct] = useState<number | null>(null)
  const [customPct, setCustomPct] = useState('')
  /** "Bu kişi için bu oranı sabit tut": bir sonraki yapılandırmada seçili gelsin. */
  const [keepFixed, setKeepFixed] = useState(false)

  const [busy, setBusy] = useState(false)
  /** Bilgisi açık borcun siparişi (Ne yedi penceresi). */
  const [infoOrder, setInfoOrder] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const shown = useMemo(() => {
    const q = fold(query.trim())
    return debtors
      .filter((d) => (showInactive || d.active) && (!q || fold(d.name).includes(q)))
      .sort((a, b) => {
        if ((a.balanceKurus > 0) !== (b.balanceKurus > 0)) {
          return a.balanceKurus > 0 ? -1 : 1
        }
        if (a.balanceKurus !== b.balanceKurus) return b.balanceKurus - a.balanceKurus
        return a.name.localeCompare(b.name, 'tr')
      })
  }, [debtors, query, showInactive])

  const selected = debtors.find((d) => d.id === selectedId) ?? null
  const history = selected ? entriesFor(selected.id) : []

  async function onCreate() {
    if (!tenantId || !newName.trim()) return
    setBusy(true)
    setError(null)
    try {
      const id = await saveDebtor(tenantId, {
        name: newName.trim(),
        phone: newPhone.trim(),
        note: newNote.trim(),
        active: true,
      })
      setSelectedId(id)
      setCreating(false)
      setNewName('')
      setNewPhone('')
      setNewNote('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
    } finally {
      setBusy(false)
    }
  }

  async function onAddEntry() {
    if (!tenantId || !selected || !entryKind) return
    const amountKurus = liraToKurus(amount)
    if (amountKurus === null || amountKurus <= 0) {
      setError('Geçerli bir tutar girin.')
      return
    }
    const by = admin?.displayName || admin?.username
    const note = description.trim()
    // İndirimli tahsilat: borçtan düşülen tutarın indirimli kısmı tahsilat,
    // kalanı ayrı bir "indirim" hareketi. İkisi tek yazımda.
    // "Borcu yapılandır" (kullanıcı, 11 Eki 2026): indirim uygulanarak kapatma.
    const pct = entryKind === 'restructure' ? discountPct : null
    if (entryKind === 'restructure' && !pct) {
      setError('Bir yapılandırma oranı seçin.')
      return
    }
    const moves: NewEntry[] = []
    if (pct) {
      const { chargedKurus, discountKurus } = applyDiscount(amountKurus, pct)
      if (chargedKurus > 0) {
        moves.push({ debtorId: selected.id, kind: 'payment', amountKurus: chargedKurus, description: note, createdByName: by })
      }
      if (discountKurus > 0) {
        moves.push({
          debtorId: selected.id,
          kind: 'discount',
          amountKurus: discountKurus,
          description: `${pctText(pct)} yapılandırma`,
          createdByName: by,
        })
      }
    } else {
      moves.push({
        debtorId: selected.id,
        kind: entryKind === 'restructure' ? 'payment' : entryKind,
        amountKurus,
        description: note,
        createdByName: by,
      })
    }
    setBusy(true)
    setError(null)
    try {
      await addEntries(tenantId, moves)
      // Sabit oran: işaretliyse bu oran kişiye yazılır, işaret kaldırıldıysa silinir.
      if (entryKind === 'restructure') {
        const next = keepFixed && pct ? pct : undefined
        if (next !== selected.discountPercent) await saveDebtor(tenantId, { ...selected, discountPercent: next })
      }
      setEntryKind(null)
      setAmount('')
      setDescription('')
      setDiscountPct(null)
      setCustomPct('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
    } finally {
      setBusy(false)
    }
  }

  async function onToggleActive(d: DebtorBalance) {
    if (!tenantId) return
    try {
      await saveDebtor(tenantId, { ...d, active: !d.active })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
    }
  }

  async function onDelete(d: DebtorBalance) {
    if (!tenantId) return
    try {
      await deleteDebtor(tenantId, d.id)
      setSelectedId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silinemedi.')
    }
  }

  if (loading) return <Skeleton kind="list" label="Cari hesaplar yükleniyor" />

  return (
    <div className="cari">
      <header className="cari__head">
        <div>
          <h1>Cari Hesap</h1>
          <p className="cari__total">
            Toplam alacak <strong>{formatTRY(totalOwedKurus)}</strong>
          </p>
        </div>
        {canSettle && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setCreating(true)
              setNewName(query.trim())
            }}
          >
            <PlusIcon />
            Yeni kişi
          </button>
        )}
      </header>

      {error && <p className="cari__error">{error}</p>}

      <div className="cari__body">
        <section className="cari__list-panel">
          <div className="debtors__search">
            <SearchIcon />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="İsim ara"
              autoComplete="off"
            />
          </div>

          {shown.length === 0 ? (
            <p className="cari__empty">
              {query ? 'Bu isimde kayıt yok.' : 'Henüz cari hesap açılmamış.'}
            </p>
          ) : (
            <ul className="cari__list">
              {shown.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className={`cari__row ${d.id === selectedId ? 'is-active' : ''} ${
                      d.active ? '' : 'is-inactive'
                    }`}
                    onClick={() => setSelectedId(d.id)}
                  >
                    <span className="cari__row-name">
                      {d.name}
                      {!d.active && <em> (pasif)</em>}
                    </span>
                    <span
                      className={`cari__row-bal ${
                        d.balanceKurus > 0 ? 'is-owed' : d.balanceKurus < 0 ? 'is-credit' : ''
                      }`}
                    >
                      {d.balanceKurus === 0
                        ? '-'
                        : d.balanceKurus > 0
                          ? formatTRY(d.balanceKurus)
                          : `+${formatTRY(-d.balanceKurus)}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label className="cari__inactive-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Pasif kayıtları da göster
          </label>
        </section>

        <section className="cari__detail">
          {!selected ? (
            <p className="cari__empty">Hareketlerini görmek için bir kişi seçin.</p>
          ) : (
            <>
              <div className="cari__person">
                <div>
                  <h2>{selected.name}</h2>
                  {selected.phone && <p className="cari__phone">{selected.phone}</p>}
                  {/* Yazılıp kaybolan bir alan olmasın: açıklama kaydedildiği
                      yerde değil, kişiye bakıldığı yerde işe yarıyor. */}
                  {selected.note && <p className="cari__note">{selected.note}</p>}
                  {selected.discountPercent && (
                    <p className="cari__fixed">Sabit yapılandırma oranı {pctText(selected.discountPercent)}</p>
                  )}
                </div>
                <div
                  className={`cari__balance ${
                    selected.balanceKurus > 0
                      ? 'is-owed'
                      : selected.balanceKurus < 0
                        ? 'is-credit'
                        : ''
                  }`}
                >
                  <span>
                    {selected.balanceKurus < 0 ? 'Alacaklı' : 'Borç'}
                  </span>
                  <strong>{formatTRY(Math.abs(selected.balanceKurus))}</strong>
                </div>
              </div>

              {canSettle && (
                <div className="cari__actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => {
                      setEntryKind('payment')
                      // The usual payment is the whole balance, so it starts
                      // filled in, a part payment is the exception.
                      setAmount(
                        selected.balanceKurus > 0
                          ? (selected.balanceKurus / 100).toFixed(2).replace('.', ',')
                          : '',
                      )
                      setDescription('')
                      setError(null)
                    }}
                  >
                    {/* Borcu yokken alınan para avans (kullanıcı, 2 Kas 2026:
                        "cariye bakiye de tanımlanabilir"). */}
                    {selected.balanceKurus > 0 ? 'Tahsilat al' : 'Bakiye yükle'}
                  </button>
                  {/* Oran uygulayarak kapatma. */}
                  {selected.balanceKurus > 0 && (
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setEntryKind('restructure')
                        setAmount((selected.balanceKurus / 100).toFixed(2).replace('.', ','))
                        setDescription('')
                        setDiscountPct(selected.discountPercent ?? null)
                        setKeepFixed(selected.discountPercent !== undefined)
                        setCustomPct('')
                        setError(null)
                      }}
                    >
                      Borcu yapılandır
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => {
                      setEntryKind('debt')
                      setAmount('')
                      setDescription('')
                      setError(null)
                    }}
                  >
                    Borç ekle
                  </button>
                </div>
              )}

              {entryKind && (
                <div className="cari__entry-form">
                  <h3>{entryKind === 'payment' ? (selected.balanceKurus > 0 ? 'Tahsilat' : 'Bakiye yükle (avans)') : entryKind === 'restructure' ? 'Borcu yapılandır' : 'Borç'}</h3>
                  <div className="cari__entry-row">
                    <label>
                      {entryKind === 'restructure' ? 'Borçtan düşülecek tutar' : 'Tutar'}
                      <input
                        value={amount}
                        onChange={(e) => setAmount(amountInput(e.target.value))}
                        inputMode="decimal"
                      />
                    </label>
                    <label>
                      Açıklama
                      <input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </label>
                  </div>
                  {entryKind === 'restructure' && (() => {
                    const fixed = selected.discountPercent
                    const presets = [...new Set([...discounts, ...(fixed ? [fixed] : [])])].sort((a, b) => a - b)
                    const amountKurus = liraToKurus(amount) ?? 0
                    const calc = discountPct && amountKurus > 0 ? applyDiscount(amountKurus, discountPct) : null
                    const pick = (p: number | null) => {
                      setDiscountPct(p)
                      setCustomPct('')
                    }
                    return (
                      <div className="cari__disc">
                        <span className="cari__disc-label">Yapılandırma oranı</span>
                        <div className="cari__disc-chips">
                          {presets.map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={discountPct === p && !customPct ? 'is-on' : ''}
                              onClick={() => pick(p)}
                            >
                              {pctText(p)}
                              {p === fixed && <small> (sabit)</small>}
                            </button>
                          ))}
                          <label className={`cari__disc-custom ${customPct ? 'is-on' : ''}`}>
                            <span>%</span>
                            <input
                              value={customPct}
                              onChange={(e) => {
                                const v = amountInput(e.target.value, true)
                                setCustomPct(v)
                                setDiscountPct(parsePct(v))
                              }}
                              inputMode="decimal"
                              placeholder="Diğer"
                              aria-label="Elle yapılandırma yüzdesi"
                            />
                          </label>
                        </div>

                        {calc && (
                          <div className="cari__disc-sum">
                            <div>
                              <span>Borçtan düşülecek</span>
                              <span>{formatTRY(amountKurus)}</span>
                            </div>
                            <div>
                              <span>Yapılandırma ({pctText(discountPct!)})</span>
                              <span>−{formatTRY(calc.discountKurus)}</span>
                            </div>
                            <div className="cari__disc-total">
                              <span>Tahsil edilecek</span>
                              <strong>{formatTRY(calc.chargedKurus)}</strong>
                            </div>
                          </div>
                        )}

                        {discountPct && (
                          <label className="cari__disc-save">
                            <input
                              type="checkbox"
                              checked={keepFixed}
                              onChange={(e) => setKeepFixed(e.target.checked)}
                            />
                            Bu kişi için bu oranı sabit tut
                          </label>
                        )}

                      </div>
                    )
                  })()}
                  {/* Önce/sonra (2 Kas 2026): borçtan fazla tahsilat avans
                      (alacak) olur, sonraki cari satışlardan düşer. */}
                  {(entryKind === 'payment' || entryKind === 'debt') && (liraToKurus(amount) ?? 0) > 0 && (
                    <BalanceAfter
                      before={selected.balanceKurus}
                      after={
                        selected.balanceKurus +
                        (entryKind === 'debt' ? 1 : -1) * (liraToKurus(amount) ?? 0)
                      }
                    />
                  )}
                  <div className="cari__entry-buttons">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => {
                        setEntryKind(null)
                        setError(null)
                      }}
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={busy || !amount || (entryKind === 'restructure' && !discountPct)}
                      onClick={() => void onAddEntry()}
                    >
                      {busy
                        ? 'Kaydediliyor…'
                        : entryKind === 'restructure' && discountPct && (liraToKurus(amount) ?? 0) > 0
                          ? `${formatTRY(applyDiscount(liraToKurus(amount)!, discountPct).chargedKurus)} tahsil et`
                          : 'Kaydet'}
                    </button>
                  </div>
                </div>
              )}

              <h3 className="cari__history-head">Hareketler</h3>
              {history.length === 0 ? (
                <p className="cari__empty">Henüz hareket yok.</p>
              ) : (
                <ul className="cari__history">
                  {history.map((e) => (
                    <li key={e.id} className={`cari__move is-${e.kind}`}>
                      <div className="cari__move-main">
                        <span className="cari__move-kind">
                          {e.kind === 'debt' ? 'Borç' : e.kind === 'discount' ? 'Yapılandırma' : 'Tahsilat'}
                        </span>
                        {e.description && (
                          <span className="cari__move-desc">{e.description}</span>
                        )}
                      </div>
                      <div className="cari__move-meta">
                        <span>{asDateTime(e.createdAtLocal)}</span>
                        {e.createdByName && <span>{e.createdByName}</span>}
                      </div>
                      <span className="cari__move-amount">
                        {e.kind === 'debt' ? '+' : '−'}
                        {formatTRY(e.amountKurus)}
                      </span>
                      {/* Ne yedi (3 Kas 2026): yalnız siparişten gelen borçta; elle girilende ürün yok. */}
                      {e.orderId ? (
                        <button
                          type="button"
                          className="cari__info"
                          onClick={() => setInfoOrder(e.orderId!)}
                          aria-label="Siparişi göster"
                          title="Ne yedi"
                        >
                          <InfoIcon />
                        </button>
                      ) : (
                        <span className="cari__info-gap" aria-hidden="true" />
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {isOwner && (
                <div className="cari__person-admin">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => void onToggleActive(selected)}
                  >
                    {selected.active ? 'Pasife al' : 'Yeniden aktif et'}
                  </button>
                  {/* Only offered where it can actually work: anyone with
                      history is kept by a database guard, and the button
                      would only ever produce an error. */}
                  {history.length === 0 && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void onDelete(selected)}
                    >
                      <TrashIcon />
                      Sil
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {creating && (
        <div className="dlg" role="dialog" aria-modal="true">
          <div className="dlg__card dlg__card--ask">
            <h2>Yeni cari hesap</h2>
            <label className="dlgfield">
              <span>İsim</span>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newName.trim()) {
                    e.preventDefault()
                    void onCreate()
                  }
                }}
              />
            </label>
            <label className="dlgfield">
              <span>Telefon (isteğe bağlı)</span>
              <input
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                inputMode="tel"
              />
            </label>
            {/* Sütun veritabanında baştan vardı ama forma hiç konmamıştı.
                İki kişinin adı aynı olduğunda birbirinden ayıran şey burası:
                "market sahibi", "her cuma ödüyor", "kapıcı Ahmet'in oğlu". */}
            <label className="dlgfield">
              <span>Açıklama (isteğe bağlı)</span>
              <input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Nereden tanıdığınız, ödeme düzeni…"
              />
            </label>
            {error && <p className="dlg__warn">{error}</p>}
            <div className="dlg__buttons dlg__buttons--even">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setCreating(false)
                  setError(null)
                }}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy || !newName.trim()}
                onClick={() => void onCreate()}
              >
                {busy ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {infoOrder && tenantId && (
        <OrderInfoDialog tenantId={tenantId} orderId={infoOrder} onClose={() => setInfoOrder(null)} />
      )}
    </div>
  )
}
